import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_FRONTEND_API_KEY } from './config/api';
import { PAYMENT_ENABLED } from './config/features';
import { Box } from '@mui/material';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import CookieBanner from './components/CookieConsent/CookieBanner';
import BarionPixel from './components/Barion/BarionPixel';
import { trackPageView } from './services/analyticsService';
import CookiePolicy from './pages/CookiePolicy';
import { LegalAcceptanceProvider } from './contexts/LegalAcceptanceContext';
import TermsUpdateModal from './components/Legal/TermsUpdateModal';

// Context
import { AuthProvider } from './contexts/AuthContext';
import { LayoutProvider } from './contexts/LayoutContext';
import { GoogleMapsContext } from './contexts/GoogleMapsContext';

// Components
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';
import ProtectedRoute from './components/Auth/ProtectedRoute'; // ÚJ IMPORT: ProtectedRoute
import AdminBlogPostsPage from './components/Admin/AdminBlogPostsPage'; // ÚJ IMPORT: AdminBlogPostsPage

// Pages
import Home from './pages/Home';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ServiceProviders from './pages/ServiceProviders';
import ServiceProviderDetail from './pages/ServiceProviderDetail';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminStatsPage from './pages/Admin/AdminStatsPage';
import ProviderEditPage from './pages/Admin/ProviderEditPage';
import GalleryManager from './pages/Provider/GalleryManager';

// ÚJ: Blog oldalak importálása
import BlogListPage from './pages/Blog/BlogListPage';
import BlogPostDetailPage from './pages/Blog/BlogPostDetailPage';

import Favorites from './pages/Favorites';
import Bookings from './pages/Bookings';
import NotificationsPage from './pages/Notifications';
import ProfileEdit from './pages/Profile/ProfileEdit';
import Settings from './pages/Settings/Settings';
import Messages from './pages/Messages';
import ActivateAccount from './pages/Auth/ActivateAccount';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import ClaimProviderInvite from './pages/Auth/ClaimProviderInvite';
import ProviderInviteWelcome from './pages/Auth/ProviderInviteWelcome';
import ConfirmEmailChange from './pages/Auth/ConfirmEmailChange';
import DeclineProviderInvite from './pages/Auth/DeclineProviderInvite';
import BookingDetails from './components/Booking/BookingDetails';
import CartPage from './pages/Cart';
import PaymentStatusPage from './pages/PaymentStatusPage';
import ProviderFeaturedPage from './pages/Provider/ProviderFeaturedPage';
import EarlyOfferPage from './pages/Provider/EarlyOfferPage';
import EarlyOfferUnsubscribe from './pages/Auth/EarlyOfferUnsubscribe';
import FeaturedPaymentSuccess from './pages/Provider/FeaturedPaymentSuccess';
import FeaturedPaymentStatus from './pages/Provider/FeaturedPaymentStatus';
import ProviderStatsPage from './pages/Provider/ProviderStatsPage';
import WeddingPlannerDashboard from './pages/WeddingPlanner/WeddingPlannerDashboard';
import WeddingEventDetail from './pages/WeddingPlanner/WeddingEventDetail';
import RSVPPage from './pages/RSVP/RSVPPage';
import ProviderLead from './pages/ProviderLead';
import Reszletek from './pages/Reszletek';
import ForumHome from './pages/Forum/ForumHome';
import ForumCategory from './pages/Forum/ForumCategory';
import ForumThread from './pages/Forum/ForumThread';
import ForumCreate from './pages/Forum/ForumCreate';
import ForumModeration from './pages/Admin/ForumModeration';
import SocialRegisterComplete from './pages/Auth/SocialRegisterComplete';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ProviderTerms from './pages/ProviderTerms';
import FeedbackWidget from './components/FeedbackWidget/FeedbackWidget';


import theme from './theme';

const libraries = ["places"];

const BookingDetailsWrapper = () => {
  const { id } = useParams();
  return <BookingDetails bookingId={parseInt(id)} />;
};

function RouteTracker() {
  const location = useLocation();
  React.useEffect(() => {
    const noTrack = ['/admin', '/dashboard'].some(p => location.pathname.startsWith(p));
    if (!noTrack) trackPageView(location.pathname);
  }, [location]);
  return null;
}

function MainContent() {
  const location = useLocation();

  const isHomePage = location.pathname === '/';
  const noChrome = NO_CHROME_ROUTES.some(path => location.pathname.startsWith(path));

  const headerHeight = (isHomePage || noChrome) ? 0 : 64;

  return (
    <Box sx={{ flexGrow: 1, paddingTop: `${headerHeight}px` }}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/szolgaltatok" element={<ServiceProviders />} />
        {/* SEO landing oldalak: a "helyszin" statikus szegmens megelőzi a :categorySlug-ot */}
        <Route path="/szolgaltatok/helyszin/:countySlug" element={<ServiceProviders />} />
        <Route path="/szolgaltatok/:categorySlug" element={<ServiceProviders />} />
        <Route path="/szolgaltatok/:categorySlug/:countySlug" element={<ServiceProviders />} />
        <Route path="/szolgaltato/:slug" element={<ServiceProviderDetail />} />
        <Route path="/galeria-kezeles" element={<GalleryManager />} />
        <Route path="/kedvencek" element={<Favorites />} />
        <Route path="/foglalasok" element={<Bookings />} />
        <Route path="/foglalasok/:id" element={<BookingDetailsWrapper />} />
        <Route path="/ertesitesek" element={<NotificationsPage />} />
        <Route path="/profil" element={<Navigate to="/beallitasok?tab=0" replace />} />
        <Route path="/beallitasok" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/activate" element={<ActivateAccount />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/szolgaltatoi-udvozlo/:token/:decline" element={<ProviderInviteWelcome />} />
        <Route path="/szolgaltatoi-udvozlo" element={<ProviderInviteWelcome />} />
        <Route path="/szolgaltatoi-meghivo" element={<ClaimProviderInvite />} />
        <Route path="/email-megerosites" element={<ConfirmEmailChange />} />
        <Route path="/meghivo-elutasitas/:token" element={<DeclineProviderInvite />} />
        <Route path="/meghivo-elutasitas" element={<DeclineProviderInvite />} />
        <Route path="/social-register" element={<SocialRegisterComplete />} />
        <Route path="/kosar" element={PAYMENT_ENABLED ? <CartPage /> : <Navigate to="/" replace />} />
        <Route path="/payment/status" element={<PaymentStatusPage />} />
         {/* Nyilvános Blog útvonalak */}
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostDetailPage />} />
        <Route path="/erdeklodes" element={<ProviderLead />} />
        <Route path="/reszletek" element={<Reszletek />} />
        <Route path="/adatvedelmi-nyilatkozat" element={<PrivacyPolicy />} />
        <Route path="/felhasznalasi-feltetelek" element={<TermsOfService />} />
        <Route path="/partneri-aszf" element={<ProviderTerms />} />
        <Route path="/sutik" element={<CookiePolicy />} />
        {/* Fórum */}
        <Route path="/forum" element={<ForumHome />} />
        <Route path="/forum/uj-tema" element={<ForumCreate />} />
        <Route path="/forum/tema/:id" element={<ForumThread />} />
        <Route path="/forum/:categorySlug" element={<ForumCategory />} />

        {/* Védett útvonalak */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute requiredRole="ADMIN"><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/providers/edit/:id" element={<ProtectedRoute requiredRole={["ADMIN", "PROVIDER"]}><ProviderEditPage /></ProtectedRoute>} />
        <Route path="/profil/szolgaltato" element={<ProtectedRoute requiredRole="PROVIDER"><ProviderEditPage /></ProtectedRoute>} />
        <Route path="/admin/blogposts" element={<ProtectedRoute requiredRole="ADMIN"><AdminBlogPostsPage /></ProtectedRoute>} />
        <Route path="/admin/forum" element={<ProtectedRoute requiredRole="ADMIN"><ForumModeration /></ProtectedRoute>} />
        <Route path="/admin/statisztika" element={<ProtectedRoute requiredRole="ADMIN"><AdminStatsPage /></ProtectedRoute>} />
        <Route path="/korai-ajanlat" element={<ProtectedRoute requiredRole="PROVIDER"><EarlyOfferPage /></ProtectedRoute>} />
        <Route path="/korai-ajanlat-leiratkozas" element={<EarlyOfferUnsubscribe />} />
        <Route path="/provider/featured" element={<ProtectedRoute requiredRole="PROVIDER"><ProviderFeaturedPage /> </ProtectedRoute>} />
        <Route path="/statisztika" element={<ProtectedRoute requiredRole="PROVIDER"><ProviderStatsPage /></ProtectedRoute>} />
        <Route path="/featured/payment-success" element={<FeaturedPaymentSuccess />} />
        <Route path="/featured/payment-status"  element={<ProtectedRoute requiredUserType="PROVIDER"><FeaturedPaymentStatus /></ProtectedRoute>}/>
        <Route path="/wedding-planner"element={<ProtectedRoute><WeddingPlannerDashboard /></ProtectedRoute>}/>
        <Route path="/wedding-planner/:eventId"element={<ProtectedRoute><WeddingEventDetail /></ProtectedRoute>}/>
        <Route path="/rsvp/:token" element={<RSVPPage />} />
        {/* 404 - Must be last */}
        <Route path="*" element={<Navigate to="/" replace />} />

      </Routes>
    </Box>
  );
}


// Oldalak amelyeken nem jelenik meg a fejlec es labléc
const NO_CHROME_ROUTES = ['/erdeklodes', '/rsvp', '/reszletek'];

function AppLayout() {
  const location = useLocation();
  const noChrome = NO_CHROME_ROUTES.some(path => location.pathname.startsWith(path));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {!noChrome && <Header />}
      <MainContent />
      {!noChrome && <Footer />}
      {!noChrome && <FeedbackWidget />}
      <BarionPixel />
      <CookieBanner />
      <TermsUpdateModal />
      <RouteTracker />
    </Box>
  );
}

function App() {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_FRONTEND_API_KEY,
    libraries: libraries,
  });

  return (
    <GoogleMapsContext.Provider value={{ isLoaded, loadError }}>
    <CookieConsentProvider>
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || 'not-configured'}>
    <HelmetProvider>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <LegalAcceptanceProvider>
          <Router>
            <LayoutProvider>
              <AppLayout />
            </LayoutProvider>
          </Router>
        </LegalAcceptanceProvider>
      </AuthProvider>
    </ThemeProvider>
    </HelmetProvider>
    </GoogleOAuthProvider>
    </CookieConsentProvider>
    </GoogleMapsContext.Provider>
  );
}

export default App;